import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Users, ArrowLeft, Sparkles, Loader2, CheckCircle, Upload, FileText,
  Send, Paperclip, Pencil, CreditCard, ShieldCheck, X
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import MeetingScheduler from '@/components/MeetingScheduler';

const STATUS_LABEL = {
  drafting: { label: 'Drafting', color: 'bg-blue-500/10 text-blue-600' },
  proposal_ready: { label: 'Brief Ready', color: 'bg-amber-500/10 text-amber-600' },
  accepted: { label: 'Notifying Innovators', color: 'bg-purple-500/10 text-purple-600' },
  interest_open: { label: 'Awaiting Innovator Interest', color: 'bg-purple-500/10 text-purple-600' },
  admin_selecting: { label: 'Admin Reviewing', color: 'bg-cyan-500/10 text-cyan-600' },
  expert_selected: { label: 'Innovator Assigned', color: 'bg-orange-500/10 text-orange-600' },
  proposal_submitted: { label: 'Proposal Submitted — Review', color: 'bg-amber-500/10 text-amber-600' },
  payment_received: { label: 'Payment Received', color: 'bg-teal-500/10 text-teal-600' },
  in_progress: { label: 'In Progress', color: 'bg-green-500/10 text-green-600' },
  completed: { label: 'Completed', color: 'bg-emerald-600/10 text-emerald-700' },
};

// Minimal markdown → styled HTML. Not a full parser but handles headings, bold, lists.
function renderMarkdown(md) {
  if (!md) return '';
  let html = md
    .replace(/</g, '&lt;')
    .replace(/^# (.+)$/gm, '<h1 class="text-2xl font-bold mt-4 mb-2">$1</h1>')
    .replace(/^## (.+)$/gm, '<h2 class="text-lg font-semibold mt-5 mb-2">$1</h2>')
    .replace(/^### (.+)$/gm, '<h3 class="text-base font-semibold mt-4 mb-1">$1</h3>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>');
  const lines = html.split('\n');
  let out = [];
  let inUl = false, inOl = false;
  for (const line of lines) {
    if (/^\s*[-*] /.test(line)) {
      if (!inUl) { out.push('<ul class="list-disc pl-6 my-2 space-y-1">'); inUl = true; }
      out.push(`<li>${line.replace(/^\s*[-*] /, '')}</li>`);
    } else if (/^\s*\d+\. /.test(line)) {
      if (!inOl) { out.push('<ol class="list-decimal pl-6 my-2 space-y-1">'); inOl = true; }
      out.push(`<li>${line.replace(/^\s*\d+\. /, '')}</li>`);
    } else {
      if (inUl) { out.push('</ul>'); inUl = false; }
      if (inOl) { out.push('</ol>'); inOl = false; }
      if (line.trim() === '') out.push('<br/>');
      else if (!line.startsWith('<h')) out.push(`<p class="text-sm leading-relaxed">${line}</p>`);
      else out.push(line);
    }
  }
  if (inUl) out.push('</ul>');
  if (inOl) out.push('</ol>');
  return out.join('\n');
}

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, isAuthenticated, isIndustry, user, API, loading: authLoading } = useAuth();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [uploading, setUploading] = useState(false);
  const [paying, setPaying] = useState(false);
  const [showChat, setShowChat] = useState(true);
  const pollRef = useRef(null);

  const fetchProject = useCallback(async () => {
    try {
      const res = await fetch(`${API}/projects/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setProject(data);
        setTitleDraft(data.title || '');
      } else if (res.status === 404) {
        toast.error('Project not found');
        navigate('/industry/projects');
      }
    } finally {
      setLoading(false);
    }
  }, [API, id, token, navigate]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated || !isIndustry) {
      navigate('/industry/auth');
      return;
    }
    fetchProject();
  }, [authLoading, isAuthenticated, isIndustry, fetchProject, navigate]);

  // Poll while status is drafting
  useEffect(() => {
    if (project?.status === 'drafting') {
      pollRef.current = setInterval(fetchProject, 3000);
      return () => clearInterval(pollRef.current);
    } else if (pollRef.current) {
      clearInterval(pollRef.current);
    }
  }, [project?.status, fetchProject]);

  const sendMessage = async () => {
    if (!message.trim()) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/projects/${id}/message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: message.trim() })
      });
      if (res.ok) {
        setMessage('');
        toast.success('Updating proposal…');
        fetchProject();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Send failed');
      }
    } finally {
      setSending(false);
    }
  };

  const saveTitle = async () => {
    if (!titleDraft.trim() || titleDraft.trim() === project.title) {
      setEditingTitle(false);
      return;
    }
    const res = await fetch(`${API}/projects/${id}/title`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ title: titleDraft.trim() })
    });
    if (res.ok) {
      setEditingTitle(false);
      fetchProject();
    }
  };

  const acceptProposal = async () => {
    if (!window.confirm('Accept this Requirement Brief? Matched innovators will be notified and no further edits will be possible.')) return;
    const res = await fetch(`${API}/projects/${id}/accept`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` }
    });
    if (res.ok) {
      toast.success('Proposal accepted! Notifying experts.');
      fetchProject();
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || 'Failed');
    }
  };

  const acceptInnovatorProposal = async () => {
    if (!window.confirm("Accept the innovator's finalized Proposal? You will then release installments as milestones are met.")) return;
    const res = await fetch(`${API}/projects/${id}/accept-innovator-proposal`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` }
    });
    if (res.ok) {
      toast.success('Proposal accepted! You can now release the first installment.');
      fetchProject();
    } else {
      const err = await res.json().catch(() => ({}));
      toast.error(err.detail || 'Failed');
    }
  };

  const uploadDoc = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) { toast.error('PDF only'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('Max 10 MB'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/projects/${id}/upload-doc`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd
      });
      if (res.ok) {
        toast.success('Document attached');
        fetchProject();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Upload failed');
      }
    } finally {
      setUploading(false);
    }
  };

  const payNow = async () => {
    const amt = nextInstallment;
    if (!amt || amt < 1) { toast.error('Installment amount unavailable'); return; }
    setPaying(true);
    try {
      const ok = await loadRazorpayScript();
      if (!ok) { toast.error('Razorpay SDK failed to load'); return; }
      const orderRes = await fetch(`${API}/projects/${id}/payment/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ amount_inr: Number(amt) })
      });
      if (!orderRes.ok) {
        const err = await orderRes.json().catch(() => ({}));
        toast.error(err.detail || 'Order creation failed');
        return;
      }
      const order = await orderRes.json();
      const options = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: 'CollabHive',
        description: `Project: ${project.title}`,
        order_id: order.order_id,
        prefill: { name: user?.name || '', email: user?.email || '', contact: '' },
        theme: { color: '#059669' },
        handler: async function (response) {
          const verifyRes = await fetch(`${API}/projects/${id}/payment/verify`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify(response)
          });
          if (verifyRes.ok) {
            toast.success('Payment successful');
            fetchProject();
          } else {
            toast.error('Signature verification failed');
          }
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (resp) => { toast.error(resp.error?.description || 'Payment failed'); });
      rzp.open();
    } finally {
      setPaying(false);
    }
  };

  if (authLoading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;
  }
  if (!project) {
    return (
      <div className="min-h-screen">
        <nav className="nav-header">
          <div className="container-main py-3">
            <Link to="/industry/projects" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
              <ArrowLeft className="w-4 h-4" /> All Projects
            </Link>
          </div>
        </nav>
        <div className="container-main py-8 max-w-3xl">
          <Card><CardContent className="p-6 text-center">
            <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">Loading project…</p>
          </CardContent></Card>
        </div>
      </div>
    );
  }

  const s = STATUS_LABEL[project.status] || STATUS_LABEL.drafting;
  const canIterate = ['drafting', 'proposal_ready'].includes(project.status);
  const canAccept = project.status === 'proposal_ready';
  const canAcceptInnovatorProposal = project.status === 'proposal_submitted';
  // Industry can pay once proposal is accepted and installments remain
  const paidCount = project.installments_paid || 0;
  const totalInst = project.num_installments || 0;
  const plan = project.installment_plan_inr || [];
  const nextInstallment = plan[paidCount] || 0;
  const canPay = !!project.proposal_accepted_at && paidCount < totalInst && ['expert_selected','payment_received','in_progress'].includes(project.status);
  const messages = project.messages || [];

  return (
    <div className="min-h-screen">
      <nav className="nav-header">
        <div className="container-main py-3 flex items-center justify-between">
          <Link to="/industry/projects" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">All Projects</span>
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-md gradient-primary flex items-center justify-center">
              <Users className="w-4 h-4 text-white" />
            </div>
            <span className="text-sm font-semibold">CollabHive</span>
          </div>
        </div>
      </nav>

      <main className="container-main py-6 max-w-6xl">
        {/* Title + Status */}
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex-1">
            {editingTitle ? (
              <div className="flex items-center gap-2">
                <Input value={titleDraft} onChange={(e) => setTitleDraft(e.target.value)} className="text-lg font-semibold" />
                <Button size="sm" onClick={saveTitle}>Save</Button>
                <Button size="sm" variant="ghost" onClick={() => setEditingTitle(false)}><X className="w-4 h-4"/></Button>
              </div>
            ) : (
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl sm:text-2xl font-bold">{project.title}</h1>
                {canIterate && (
                  <Button size="icon" variant="ghost" onClick={() => setEditingTitle(true)} className="w-7 h-7">
                    <Pencil className="w-4 h-4" />
                  </Button>
                )}
              </div>
            )}
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <Badge className={s.color}>{s.label}</Badge>
              {project.status === 'drafting' && <span className="text-xs text-muted-foreground animate-pulse">Preparing your brief…</span>}
            </div>
          </div>
          {canAccept && (
            <Button onClick={acceptProposal} className="gap-2" data-testid="accept-proposal-btn">
              <CheckCircle className="w-4 h-4" />
              Accept Brief
            </Button>
          )}
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Left: Progress + Proposal */}
          <div className="lg:col-span-2 space-y-4">
            {/* Progress stepper */}
            <Card>
              <CardContent className="p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-3">Project Progress</p>
                {(() => {
                  const numInst = project.num_installments || 3;
                  const plan = project.installment_plan_inr || [];
                  const paidCount = project.installments_paid || 0;
                  const briefAccepted = ['accepted','interest_open','admin_selecting','expert_selected','proposal_submitted','payment_received','in_progress','completed'].includes(project.status);
                  const innovatorAssigned = !!project.selected_expert_id;
                  const proposalSubmitted = !!project.proposal_submitted_at;
                  const proposalAccepted = !!project.proposal_accepted_at;
                  const projectComplete = project.status === 'completed';

                  const steps = [
                    { key: 'brief', label: 'Requirement Brief', done: true, current: project.status === 'drafting' || project.status === 'proposal_ready' },
                    { key: 'brief_accept', label: 'Brief Accepted', done: briefAccepted, current: project.status === 'proposal_ready' },
                    { key: 'assigned', label: 'Innovator Assigned', done: innovatorAssigned, current: project.status === 'expert_selected' && !proposalSubmitted },
                    { key: 'prop_sub', label: 'Proposal Submitted', done: proposalSubmitted, current: project.status === 'proposal_submitted' },
                    { key: 'prop_acc', label: 'Proposal Accepted', done: proposalAccepted, current: proposalAccepted && paidCount === 0 },
                  ];
                  for (let i = 0; i < numInst; i++) {
                    const amt = plan[i] ? `₹${(plan[i]).toLocaleString('en-IN')}` : '';
                    steps.push({
                      key: `inst${i+1}`,
                      label: `Installment ${i+1}${amt ? ` (${amt})` : ''}`,
                      done: paidCount > i,
                      current: paidCount === i && proposalAccepted,
                    });
                    if (i < numInst - 1) {
                      steps.push({
                        key: `sub${i+1}`,
                        label: `Submission ${i+1}`,
                        done: paidCount > i + 1 || (paidCount === i + 1 && projectComplete),
                        current: paidCount === i + 1 && !projectComplete,
                      });
                    }
                  }
                  steps.push({ key: 'delivery', label: 'Delivery', done: projectComplete, current: projectComplete });

                  return (
                    <div className="flex items-start gap-1 overflow-x-auto pb-2">
                      {steps.map((step, i) => (
                        <div key={step.key} className="flex items-center flex-1 min-w-fit">
                          <div className="flex flex-col items-center min-w-[70px] sm:min-w-[90px]">
                            <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-[10px] sm:text-xs font-semibold border-2 shrink-0 ${
                              step.done ? (step.current ? 'bg-primary text-white border-primary animate-pulse' : 'bg-primary text-white border-primary') : 'bg-background text-muted-foreground border-muted'
                            }`}>
                              {step.done && !step.current ? '✓' : i + 1}
                            </div>
                            <span className={`text-[9px] sm:text-[10px] mt-1 text-center leading-tight ${step.done ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                              {step.label}
                            </span>
                          </div>
                          {i < steps.length - 1 && (
                            <div className={`h-0.5 mx-1 min-w-[10px] flex-1 mt-3.5 ${steps[i + 1].done ? 'bg-primary' : 'bg-muted'}`} />
                          )}
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-5 sm:p-6">
                {project.status === 'drafting' && !project.proposal_markdown ? (
                  <div className="flex items-center gap-3 text-muted-foreground py-8 justify-center">
                    <Loader2 className="w-5 h-5 animate-spin text-primary" />
                    <span>Preparing your Requirement Brief…</span>
                  </div>
                ) : project.final_proposal_markdown ? (
                  <>
                    <div className="flex items-center gap-2 mb-3">
                      <Badge className="bg-emerald-500/10 text-emerald-700">Innovator&apos;s Finalized Proposal</Badge>
                      {project.total_amount_inr > 0 && (
                        <span className="text-xs text-muted-foreground">
                          Total: ₹{project.total_amount_inr.toLocaleString('en-IN')} · {project.num_installments} installments
                        </span>
                      )}
                    </div>
                    <div dangerouslySetInnerHTML={{ __html: renderMarkdown(project.final_proposal_markdown) }} />
                    <details className="mt-6 text-sm">
                      <summary className="cursor-pointer text-muted-foreground hover:text-foreground">View original AI Requirement Brief</summary>
                      <div className="mt-3 opacity-80" dangerouslySetInnerHTML={{ __html: renderMarkdown(project.proposal_markdown) }} />
                    </details>
                  </>
                ) : (
                  <div dangerouslySetInnerHTML={{ __html: renderMarkdown(project.proposal_markdown) }} />
                )}
              </CardContent>
            </Card>

            {/* Supporting Docs */}
            {(canIterate || project.supporting_docs?.length > 0) && (
              <Card>
                <CardContent className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Paperclip className="w-4 h-4 text-muted-foreground" />
                    <h3 className="font-medium">Supporting Documents</h3>
                    <span className="text-xs text-muted-foreground">
                      ({(project.supporting_docs || []).length}/5, PDF, max 10 MB each)
                    </span>
                  </div>
                  <div className="space-y-2">
                    {(project.supporting_docs || []).map((d) => (
                      <div key={d.file_id} className="flex items-center gap-2 text-sm border rounded p-2">
                        <FileText className="w-4 h-4 text-muted-foreground" />
                        <span className="flex-1 truncate">{d.filename}</span>
                        <span className="text-xs text-muted-foreground">{(d.size / (1024 * 1024)).toFixed(1)} MB</span>
                      </div>
                    ))}
                  </div>
                  {canIterate && (project.supporting_docs || []).length < 5 && (
                    <label className="mt-3 inline-flex">
                      <Button asChild size="sm" variant="outline" className="gap-2 cursor-pointer" disabled={uploading}>
                        <span>{uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Attach PDF</span>
                      </Button>
                      <input type="file" accept="application/pdf" className="hidden" onChange={(e) => uploadDoc(e.target.files?.[0])} />
                    </label>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right: Chat / Actions */}
          <div className="space-y-4">
            {/* Chat panel (only until acceptance) */}
            {canIterate && (
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-primary" />
                      <h3 className="font-medium">Refine your Brief</h3>
                    </div>
                    <button className="text-xs text-muted-foreground" onClick={() => setShowChat(v => !v)}>
                      {showChat ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  {showChat && (
                    <>
                      <div className="max-h-72 overflow-y-auto space-y-2 mb-3 pr-1">
                        {messages.filter(m => m.role === 'user').slice(-6).map((m, i) => (
                          <div key={i} className="text-xs bg-muted/50 rounded p-2 border-l-2 border-primary/40">
                            <span className="text-muted-foreground">You: </span>{m.content}
                          </div>
                        ))}
                        {messages.length === 0 && (
                          <p className="text-xs text-muted-foreground italic">
                            Suggest changes — e.g. &quot;reduce timeline to 4 months&quot; or &quot;add stricter IP terms&quot;.
                          </p>
                        )}
                      </div>
                      <Textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Suggest a change to the requirement brief…"
                        rows={3}
                        className="mb-2 resize-none text-sm"
                        data-testid="chat-input"
                      />
                      <Button
                        onClick={sendMessage}
                        disabled={sending || !message.trim() || project.status === 'drafting'}
                        className="w-full gap-2"
                        data-testid="send-message-btn"
                      >
                        {sending || project.status === 'drafting' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        Send
                      </Button>
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Status-specific action cards */}
            {project.status === 'accepted' || project.status === 'interest_open' || project.status === 'admin_selecting' ? (
              <Card className="border-purple-500/40 bg-purple-500/5">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <ShieldCheck className="w-4 h-4 text-purple-600" />
                    <h3 className="font-medium">Matching Experts</h3>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    We&apos;re notifying the most relevant experts and gathering their expressions of interest.
                    Our admin team will then select the best-fit expert and inform you.
                  </p>
                </CardContent>
              </Card>
            ) : null}

            {canAcceptInnovatorProposal && (
              <Card className="border-amber-500/40 bg-amber-500/5">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-4 h-4 text-amber-600" />
                    <h3 className="font-medium">Innovator&apos;s Proposal Ready</h3>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    Your assigned innovator has submitted a finalized Proposal (see above), including
                    the total project value and installment plan. Review and accept to proceed to payment.
                  </p>
                  {project.total_amount_inr > 0 && (
                    <div className="rounded-md border bg-background/60 p-3 mb-3 text-xs space-y-1">
                      <div className="flex justify-between"><span className="text-muted-foreground">Total project value</span><span className="font-semibold">₹{project.total_amount_inr.toLocaleString('en-IN')}</span></div>
                      <div className="flex justify-between"><span className="text-muted-foreground">Installments</span><span className="font-semibold">{project.num_installments}</span></div>
                      {(project.installment_plan_inr || []).map((amt, i) => (
                        <div key={i} className="flex justify-between text-muted-foreground">
                          <span>· Installment {i+1}</span>
                          <span>₹{amt.toLocaleString('en-IN')}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  <Button onClick={acceptInnovatorProposal} className="w-full gap-2" data-testid="accept-innovator-proposal-btn">
                    <CheckCircle className="w-4 h-4" />
                    Accept Proposal
                  </Button>
                </CardContent>
              </Card>
            )}

            {project.status === 'expert_selected' && !project.proposal_submitted_at && (
              <Card className="border-orange-500/40 bg-orange-500/5">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-4 h-4 text-orange-600" />
                    <h3 className="font-medium">Innovator Assigned</h3>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    An innovator has been matched with your project. They are preparing a finalized Proposal
                    (edited scope, total cost, and milestone plan). You&apos;ll be notified when it&apos;s ready to review.
                  </p>
                </CardContent>
              </Card>
            )}

            {canPay && (
              <Card className="border-orange-500/40 bg-orange-500/5">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CreditCard className="w-4 h-4 text-orange-600" />
                    <h3 className="font-medium">
                      {paidCount === 0 ? 'First Installment' : `Installment ${paidCount + 1} of ${totalInst}`}
                    </h3>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    Release the {paidCount === 0 ? 'first' : 'next'} installment to CollabHive to
                    {paidCount === 0 ? ' formally kick off the work.' : ' unlock the next milestone.'}
                  </p>

                  <div className="rounded-md border bg-background/60 p-3 mb-3">
                    <p className="text-xs font-semibold uppercase tracking-wide text-primary mb-2">CollabHive Assurance</p>
                    <ul className="text-xs space-y-1 text-muted-foreground">
                      <li>🛡️ <b>Escrow protection</b> — funds released to the innovator only after our admin verifies milestone progress</li>
                      <li>🔁 <b>Innovator replacement</b> — if performance is unsatisfactory, we&apos;ll assign an alternate at no extra cost</li>
                      <li>📅 <b>Milestone-based payouts</b> — future installments are tied to signed deliverables</li>
                      <li>⚖️ <b>Neutral admin oversight &amp; dispute resolution</b> — CollabHive mediates any conflict</li>
                      <li>🔒 <b>Payments via Razorpay</b> — bank-grade encryption, PCI-DSS compliant</li>
                    </ul>
                  </div>

                  <div className="rounded-md border bg-background/60 p-3 mb-3 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Amount due now</span>
                      <span className="text-lg font-bold">₹{nextInstallment.toLocaleString('en-IN')}</span>
                    </div>
                    {project.total_amount_inr > 0 && (
                      <p className="text-[11px] text-muted-foreground mt-1">
                        of ₹{project.total_amount_inr.toLocaleString('en-IN')} total · installment {paidCount + 1} / {totalInst}
                      </p>
                    )}
                  </div>

                  <Button onClick={payNow} disabled={paying || !nextInstallment} className="w-full gap-2" data-testid="pay-now-btn">
                    {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                    Pay ₹{nextInstallment.toLocaleString('en-IN')} Securely with Razorpay
                  </Button>
                </CardContent>
              </Card>
            )}

            {['payment_received', 'in_progress', 'completed'].includes(project.status) && (
              <Card className="border-green-500/40 bg-green-500/5">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <h3 className="font-medium">
                      {project.status === 'in_progress' ? 'Project In Progress' :
                       project.status === 'completed' ? 'Project Completed' : 'Payment Received'}
                    </h3>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {project.status === 'payment_received' && 'Kickoff underway. You&apos;ll get milestone updates shortly.'}
                    {project.status === 'in_progress' && 'Your innovator is working on the project. Milestone updates will follow.'}
                    {project.status === 'completed' && 'This project has been marked complete.'}
                  </p>
                  {project.amount_paid_paise > 0 && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Paid: ₹{(project.amount_paid_paise / 100).toLocaleString('en-IN')}
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Virtual Meetings — available once innovator is assigned */}
            {project.selected_expert_id && ['expert_selected','payment_received','in_progress','completed'].includes(project.status) && (
              <MeetingScheduler projectId={project.id} token={token} user={user} API={API} />
            )}

            {/* Milestone submissions from innovator (read-only for industry) */}
            {project.proposal_accepted_at && (
              <IndustrySubmissionsView project={project} API={API} token={token} />
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function IndustrySubmissionsView({ project, API, token }) {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/projects/${project.id}/submissions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const d = await res.json();
          setSubs(d.submissions || []);
        }
      } finally { setLoading(false); }
    })();
  }, [API, project.id, token, project.updated_at]);

  if (loading) return null;
  if (subs.length === 0) {
    return (
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-1">
            <FileText className="w-4 h-4 text-muted-foreground" />
            <h3 className="font-medium">Milestone Submissions</h3>
          </div>
          <p className="text-xs text-muted-foreground">No milestone deliveries yet. Your innovator will upload them after each installment is released.</p>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <FileText className="w-4 h-4 text-primary" />
          <h3 className="font-medium">Milestone Submissions</h3>
        </div>
        <div className="space-y-2">
          {subs.map((s) => (
            <div key={s.id} className="border rounded p-3 bg-muted/20" data-testid={`industry-submission-${s.id}`}>
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <Badge className="bg-emerald-500/10 text-emerald-700">Milestone {s.submission_number}</Badge>
                <span className="text-[10px] text-muted-foreground">{new Date(s.submitted_at).toLocaleString()}</span>
              </div>
              {s.notes && <p className="text-sm text-muted-foreground mb-2">{s.notes}</p>}
              <div className="flex flex-col gap-1 text-sm">
                {s.file && (
                  <a href={`${API}/projects/${project.id}/submission-file/${s.file.file_id}`}
                     target="_blank" rel="noreferrer"
                     className="text-primary hover:underline inline-flex items-center gap-1">
                    <FileText className="w-4 h-4" /> {s.file.filename}
                  </a>
                )}
                {s.link && (
                  <a href={s.link} target="_blank" rel="noreferrer" className="text-primary hover:underline break-all">
                    {s.link}
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
